/**
 * J1/J3 · Microphone capture + on-device pitch tracking loop.
 * Gain is normalized via constraints + software AGC reference; a noise-floor
 * check before recording warns the student when the room is too loud (J3).
 */

import {
  DEFAULT_CLARITY_THRESHOLD,
  DEFAULT_RMS_GATE,
  detectPitch,
  PitchSmoother,
  rmsLevel,
  type TracePoint,
} from '@solfo/shared';
import { createPitchWorkletNode, ensurePitchWorklet, type WorkletPitchMessage } from './pitch-worklet.ts';
import { audioContext, playClick } from './synth.ts';

const WINDOW = 2048;
export const NOISE_FLOOR_WARN_RMS = 0.045;

export class MicSession {
  private stream: MediaStream | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private analyser: AnalyserNode | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private workletSink: GainNode | null = null;
  private raf = 0;
  private smoother = new PitchSmoother();
  private buf = new Float32Array(WINDOW);
  recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  /** AudioContext time at which the recorder actually began capturing. */
  recordingStartedAt: number | null = null;

  async open(): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: false, // we want the raw voice, not telephony processing
        noiseSuppression: false,
        autoGainControl: true, // J3 gain normalization
        channelCount: 1,
      },
    });
    const ac = audioContext();
    this.source = ac.createMediaStreamSource(this.stream);
    // analyser stays for the noise-floor check + as the tracking fallback
    this.analyser = ac.createAnalyser();
    this.analyser.fftSize = WINDOW;
    this.source.connect(this.analyser);

    // J1 · preferred path: detection on the audio thread (worklet + WASM)
    if (await ensurePitchWorklet(ac)) {
      try {
        this.workletNode = createPitchWorkletNode(ac, {
          clarityThreshold: DEFAULT_CLARITY_THRESHOLD,
          rmsGate: DEFAULT_RMS_GATE,
        });
        // silent sink keeps the node in the render graph on every browser
        this.workletSink = ac.createGain();
        this.workletSink.gain.value = 0;
        this.source.connect(this.workletNode).connect(this.workletSink).connect(ac.destination);
      } catch {
        this.workletNode = null;
      }
    }
  }

  /** J3 · Sample ambient noise for `ms` before the exercise starts. */
  async measureNoiseFloor(ms = 700): Promise<number> {
    if (!this.analyser) throw new Error('Mic not open');
    const samples: number[] = [];
    const end = performance.now() + ms;
    while (performance.now() < end) {
      this.analyser.getFloatTimeDomainData(this.buf);
      samples.push(rmsLevel(this.buf));
      await new Promise((r) => setTimeout(r, 60));
    }
    samples.sort((a, b) => a - b);
    return samples[Math.floor(samples.length / 2)] ?? 0;
  }

  /**
   * Start the tracking loop. `clock` maps to exercise time (seconds since
   * beat 1); `latencyOffsetMs` (J2 calibration) shifts samples back so the
   * trace aligns with what the student actually sang.
   */
  startTracking(
    clock: () => number,
    onPoint: (p: TracePoint) => void,
    latencyOffsetMs = 0,
  ): void {
    if (!this.analyser) throw new Error('Mic not open');
    this.smoother.reset();
    const ac = audioContext();

    if (this.workletNode) {
      // results carry the audio-clock time of the analyzed window, so a busy
      // main thread delays delivery without skewing where the point lands
      this.workletNode.port.onmessage = (e: MessageEvent<WorkletPitchMessage>) => {
        const msg = e.data;
        const midi = this.smoother.push(msg.midi);
        const t = clock() - (ac.currentTime - msg.time) - latencyOffsetMs / 1000;
        if (t >= 0) onPoint({ t, midi, clarity: msg.clarity });
      };
      this.workletNode.port.postMessage('start');
      return;
    }

    // fallback: main-thread analyser polling (pre-worklet browsers)
    const loop = () => {
      this.analyser!.getFloatTimeDomainData(this.buf);
      const res = detectPitch(this.buf, ac.sampleRate, {
        clarityThreshold: DEFAULT_CLARITY_THRESHOLD,
      });
      if (res) {
        const midi = this.smoother.push(res.midi);
        const t = clock() - latencyOffsetMs / 1000;
        if (t >= 0) onPoint({ t, midi, clarity: res.clarity });
      }
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  stopTracking(): void {
    if (this.workletNode) {
      this.workletNode.port.postMessage('stop');
      this.workletNode.port.onmessage = null;
    }
    cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  // ── D1 · Take recording ────────────────────────────────────────────────

  startRecording(): void {
    if (!this.stream) throw new Error('Mic not open');
    this.chunks = [];
    const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : 'audio/mp4';
    this.recorder = new MediaRecorder(this.stream, { mimeType: mime });
    this.recordingStartedAt = null;
    this.recorder.onstart = () => {
      // capture on the SAME clock the playback is scheduled on, so the take
      // can store how far into the file the exercise's beat 1 falls
      this.recordingStartedAt = audioContext().currentTime;
    };
    this.recorder.ondataavailable = (e) => e.data.size > 0 && this.chunks.push(e.data);
    this.recorder.start(250);
  }

  async stopRecording(): Promise<Blob> {
    const rec = this.recorder;
    if (!rec) throw new Error('Not recording');
    const done = new Promise<void>((resolve) => {
      rec.onstop = () => resolve();
    });
    rec.stop();
    await done;
    this.recorder = null;
    return new Blob(this.chunks, { type: rec.mimeType.split(';')[0] });
  }

  close(): void {
    this.stopTracking();
    if (this.recorder && this.recorder.state !== 'inactive') this.recorder.stop();
    this.workletNode?.disconnect();
    this.workletSink?.disconnect();
    this.source?.disconnect();
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    this.source = null;
    this.analyser = null;
    this.workletNode = null;
    this.workletSink = null;
  }
}

/**
 * J2 · Latency calibration: play a click, watch the mic for its arrival, and
 * report the round-trip offset. Median of `rounds` runs.
 */
export async function calibrateLatency(rounds = 3): Promise<number> {
  const mic = new MicSession();
  await mic.open();
  const ac = audioContext();
  const analyser = (mic as unknown as { analyser: AnalyserNode }).analyser;
  const buf = new Float32Array(2048);
  const offsets: number[] = [];
  try {
    for (let r = 0; r < rounds; r++) {
      const baseline = await mic.measureNoiseFloor(300);
      const threshold = Math.max(baseline * 4, 0.02);
      const clickAt = ac.currentTime + 0.12;
      playClick(clickAt, true);
      const deadline = performance.now() + 900;
      const clickPerfTime = performance.now() + (clickAt - ac.currentTime) * 1000;
      let detected: number | null = null;
      while (performance.now() < deadline) {
        analyser.getFloatTimeDomainData(buf);
        if (rmsLevel(buf) > threshold && performance.now() > clickPerfTime) {
          detected = performance.now() - clickPerfTime;
          break;
        }
        await new Promise((res) => setTimeout(res, 4));
      }
      if (detected !== null) offsets.push(detected);
      await new Promise((res) => setTimeout(res, 250));
    }
  } finally {
    mic.close();
  }
  if (offsets.length === 0) throw new Error('Could not hear the calibration click — check your volume.');
  offsets.sort((a, b) => a - b);
  return Math.round(Math.min(450, offsets[Math.floor(offsets.length / 2)]!));
}
